import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

interface PersonalData {
  firstName: string;
  lastName: string;
  fullName: string;
  ssn: string;
  email: string;
  phoneNo: string;
  country: string;
  city: string;
  address: string;
  zipCode: string;
}

interface EmployeeInfoSectionProps {
  personalData: PersonalData;
}

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <div className="ec-field-group">
    <label className="ec-field-label">{label}</label>
    <input className="ec-field-input" value={value || ''} disabled readOnly />
  </div>
);

const SkeletonField: React.FC = () => (
  <div className="ec-field-group">
    <div className="ec-skeleton ec-skeleton--label" />
    <div className="ec-skeleton ec-skeleton--input" />
  </div>
);

export const EmployeeInfoSection: React.FC<EmployeeInfoSectionProps> = ({ personalData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';
  const isLoading = useSelector((state: any) => state.profileSettings.isLoading);

  const displayFullName = [personalData.firstName, personalData.lastName]
    .filter(Boolean)
    .join(' ') || personalData.fullName || '';

  const handleAccountSettings = () => {
    navigate(`/profile-settings?lang=${lang}`);
  };

  return (
    <div className="ec-section-card">
      <div className="ec-section-head">
        <span className="ec-section-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
            <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
        <h3 className="ec-section-title">{t('EMPLOYEE_INFORMATION') || 'Employee Information'}</h3>
        <button className="ec-account-settings-btn" onClick={handleAccountSettings}>
          {t('ACCOUNT_SETTINGS') || 'Account Settings'}
        </button>
      </div>
      <Row>
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <Col md={4} key={i}>
              <SkeletonField />
            </Col>
          ))
        ) : (
          <>
            <Col md={4}>
              <InfoField label={t('FULL_NAME') || 'Full Name'} value={displayFullName} />
            </Col>
            <Col md={4}>
              <InfoField label={t('SOCIAL_SECURITY_NUMBER') || 'Social Security Number'} value={personalData.ssn} />
            </Col>
            <Col md={4}>
              <InfoField label={t('EMAIL') || 'Email'} value={personalData.email} />
            </Col>
            <Col md={4}>
              <InfoField label={t('PHONE_NUMBER') || 'Phone Number'} value={personalData.phoneNo} />
            </Col>
            <Col md={4}>
              <InfoField label={t('COUNTRY') || 'Country'} value={personalData.country} />
            </Col>
            <Col md={4}>
              <InfoField label={t('CITY') || 'City'} value={personalData.city} />
            </Col>
            <Col md={4}>
              <InfoField label={t('ADDRESS') || 'Address'} value={personalData.address} />
            </Col>
            <Col md={4}>
              <InfoField label={t('POSTAL_CODE') || 'Postal Code'} value={personalData.zipCode} />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};
